import { type CanvasConfig } from './types';

/** Default title assigned to newly created canvases */
export const DEFAULT_CANVAS_TITLE = 'Untitled Canvas';

/** Default viewport for the canvas */
export const DEFAULT_VIEWPORT = { x: 0, y: 0, zoom: 1 } as const;

/** Minimum number of nodes required for distribution */
export const MINIMUM_DISTRIBUTION_COUNT = 3;

/** Size of the thumbnail in the token chip */
export const TOKEN_CHIP_THUMBNAIL_SIZE = 24;

/** Spacing between grid dots (px) */
export const GRID_SIZE = 24;

/** Minimum zoom level for the canvas */
export const MIN_ZOOM = 0.01;

/** Maximum zoom level for the canvas */
export const MAX_ZOOM = 2;

/** Dot radius at minimum zoom */
export const MIN_DOT_RADIUS = 0.25;

/** Dot radius at maximum zoom */
export const MAX_DOT_RADIUS = 1;

/** Screen-space gap (px) at which dots are fully hidden */
export const MIN_GAP_PX = 4;

/** Screen-space gap (px) at which dots are fully visible */
export const FULL_GAP_PX = 16;

/** Exponent for the density-compensation fade curve (lower = gentler fade at low zoom) */
export const DENSITY_FADE_EXPONENT = 0.05;

/** Base HSL lightness for dot color in light mode (--colors-gray-9) */
export const DOT_LIGHTNESS_LIGHT = 50;

/** Base HSL lightness for dot color in dark mode (--colors-gray-9) */
export const DOT_LIGHTNESS_DARK = 64;

/** Lightness percentage points shifted across the full zoom range */
export const DOT_LIGHTNESS_SHIFT = 4;

/** Default size of the ImageGeneration node */
export const IMAGE_GENERATION_NODE_SIZE = 1024;

/** Standard gap between nodes for layout computations (matches grid dot spacing) */
export const NODE_GAP = 24;

/** Default maximum row width before wrapping to a new row (fallback when viewport unknown) */
export const DEFAULT_MAX_ROW_WIDTH = 64000;

/** Minimum screen-space dimension (px) before showing hover buttons on a node */
export const MIN_NODE_SCREEN_SIZE_FOR_HOVER_BUTTONS = 80;

/** Vertical gap between floating toolbar and selection bounds (px) */
export const TOOLBAR_GAP = 24;

/** Z-index for floating toolbars (multi-select, group) */
export const TOOLBAR_Z_INDEX = 10;

/** CSS custom property set by SidebarResizer for the chat sidebar width */
export const CHAT_SIDEBAR_WIDTH_CSS_VAR = '--chat-sidebar-width';

/** localStorage key for chat sidebar width */
export const CHAT_SIDEBAR_WIDTH_LOCAL_STORAGE_KEY = 'CHAT_SIDEBAR_WIDTH';

/** Mouse button indices for ReactFlow's panOnDrag */
const LEFT_MOUSE_BUTTON = 0;
const MIDDLE_MOUSE_BUTTON = 1;

/** Node types that are selectable */
export const SELECTABLE_NODE_TYPES = new Set(['image', 'video', 'text']);

/** Standard interactive mode: nodes are selectable/draggable, pan with middle mouse, selection drag enabled */
export const DEFAULT_CANVAS_CONFIG: CanvasConfig = {
  elementsSelectable: true,
  nodesDraggable: true,
  panOnDrag: [MIDDLE_MOUSE_BUTTON],
  selectionOnDrag: true,
};

/** View-only select mode: nodes are selectable but not draggable, pan with middle mouse */
export const READONLY_CANVAS_CONFIG: CanvasConfig = {
  elementsSelectable: true,
  nodesDraggable: false,
  panOnDrag: [MIDDLE_MOUSE_BUTTON],
  selectionOnDrag: true,
};

/** Spread on top of per-transform configs: restricts pan to middle mouse and disables double-click zoom to avoid interfering with transform interactions */
export const TRANSFORM_CANVAS_CONFIG: CanvasConfig = {
  panOnDrag: [MIDDLE_MOUSE_BUTTON],
  zoomOnDoubleClick: false,
};

/** Hand (grab) tool active: disables selection/dragging so all mouse buttons pan the canvas */
export const HAND_TOOL_CANVAS_CONFIG: CanvasConfig = {
  elementsSelectable: false,
  nodesDraggable: false,
  panOnDrag: [LEFT_MOUSE_BUTTON, MIDDLE_MOUSE_BUTTON],
  selectionOnDrag: false,
};
